import type { ReactNode } from "react";

import { ApiError } from "@/api/client";

import Unauthorized from "./Unauthorized";

interface QueryLike {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

interface Props {
  query: QueryLike;
  isEmpty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
}

function statusOf(error: unknown): number | undefined {
  if (error instanceof ApiError) return error.status;
  return (error as { status?: number })?.status;
}

export default function QueryState({
  query,
  isEmpty = false,
  emptyMessage = "No hay contenido disponible.",
  children,
}: Props) {
  if (query.isLoading) {
    return (
      <div
        className="flex min-h-[40vh] items-center justify-center px-4 py-16 text-base text-[#00402a]/70 sm:px-5 sm:py-20"
        role="status"
      >
        Cargando…
      </div>
    );
  }

  if (query.isError) {
    if (statusOf(query.error) === 401) return <Unauthorized />;
    if (statusOf(query.error) === 404) {
      return (
        <div className="pulse-state-panel rounded-2xl border border-[#ebd9ca] bg-white p-6 text-center text-base text-[#00402a]/70 sm:p-8 md:p-10">
          {emptyMessage}
        </div>
      );
    }
    return (
      <div
        className="pulse-state-panel rounded-2xl border border-red-200 bg-red-50 p-5 text-base text-red-700 sm:p-6"
        role="alert"
      >
        Ha ocurrido un error al cargar el contenido. Inténtalo de nuevo más tarde.
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="pulse-state-panel rounded-2xl border border-[#ebd9ca] bg-white p-6 text-center text-base text-[#00402a]/70 sm:p-8 md:p-10">
        {emptyMessage}
      </div>
    );
  }

  return <>{children}</>;
}
